import React, { useState } from "react";

/**
 * CellWithTooltip - Componente para mostrar texto truncado con tooltip
 *
 * Variantes de iconos disponibles para reemplazar ⓘ:
 * - ⓘ  (información - actual)
 * - ⋮  (tres puntos verticales)
 * - ⋯  (tres puntos horizontales)
 * - ↓  (flecha hacia abajo)
 * - +  (plus simple)
 * - 📄 (documento)
 * - 👁️ (ojo)
 * - ▼  (triángulo hacia abajo)
 *
 * Para cambiar el icono, modificar la línea con el icono ⓘ
 */

export function CellWithTooltip({ children, maxLength = 50, className = "" }) {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!children) return null;

  const rawText = typeof children === "string" ? children : String(children);

  // Limpiar y formatear el texto
  const cleanText = rawText
    .replace(/\r\n/g, "\n") // Normalizar saltos de línea
    .replace(/\*+/g, " • ") // Convertir asteriscos en viñetas
    .replace(/\s+/g, " ") // Limpiar espacios múltiples
    .trim(); // Quitar espacios al inicio/final

  const shouldTruncate = cleanText.length > maxLength;
  const truncatedText = shouldTruncate
    ? cleanText.substring(0, maxLength)
    : cleanText;

  if (!shouldTruncate) {
    return <span className={className}>{cleanText}</span>;
  }

  // Preparar texto para el tooltip (con saltos de línea formateados)
  const tooltipText = rawText
    .replace(/\r\n/g, "\n") // Normalizar saltos de línea
    .replace(/\*+/g, "\n• ") // Convertir asteriscos en lista con viñetas
    .replace(/^\n• /, "• ") // Quitar salto al inicio si empieza con asterisco
    .replace(/\n+/g, "\n") // Limpiar múltiples saltos
    .trim();

  return (
    <div className="relative inline-block">
      <span
        className={`cursor-help ${className}`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {truncatedText}
        {shouldTruncate && (
          <span className="ml-1 text-blue-500 hover:text-blue-600 text-xs font-medium">
            ⓘ
          </span>
        )}
      </span>

      {showTooltip && (
        <div
          className="absolute z-50 p-3 mt-1 text-sm text-white bg-gray-800 rounded shadow-lg 
                        min-w-max max-w-sm whitespace-pre-line break-words -top-2 left-0 
                        transform -translate-y-full
                        before:content-[''] before:absolute before:top-full before:left-4 
                        before:border-4 before:border-transparent before:border-t-gray-800"
        >
          {tooltipText}
        </div>
      )}
    </div>
  );
}
